import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Injector,
    Input,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FiProfileSizes, IUserProfile } from '../../profile-icon/profile-icon.component';
import { MAT_AUTOCOMPLETE_DEFAULT_OPTIONS, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { SelectSearchBase } from '../select-search-base';
import { TextFontSize } from '../fi-select-search-item/fi-select-search-item.component';
import { isDefined, isEqual } from '../../utils';
import { FiBrowserEventsService } from '../../fi-shared-services/fi-browser-events.service';
import { takeUntil } from 'rxjs/operators';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import {
    FiDropdownActionGuard,
    FiDropdownActionResult,
    FiDropdownItemDisabledFn,
    FiDropdownItemTitleFn,
} from '../fi-select-search';
import { MatOptionSelectionChange } from '@angular/material/core';
import { FiChipsSelectSearchHelperService } from '@fi-shared/fi-select-search/fi-chips-select-search/fi-chips-select-search-helper.service';
import { MatChipGrid } from '@angular/material/chips';

@Component({
    selector: 'fi-chips-select-search',
    templateUrl: './fi-chips-select-search.component.html',
    styleUrls: ['./fi-chips-select-search.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-chips-select-search' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FiChipsSelectSearchComponent,
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: FiChipsSelectSearchComponent,
            multi: true,
        },
        {
            provide: MAT_AUTOCOMPLETE_DEFAULT_OPTIONS,
            deps: [FiChipsSelectSearchComponent],
            useFactory: (fiChipsSelectSearchComponent) => ({
                overlayPanelClass: fiChipsSelectSearchComponent.isAutoCompleteWidthPanelWidthUnset ? 'width-unset' : '',
            }),
        },
    ],
    standalone: false,
})
export class FiChipsSelectSearchComponent<T, TRes = number> extends SelectSearchBase<T, TRes[]> implements OnInit {
    // @Input() ngModel: T; we already have it, comes from ControlValueAccessorBase

    @Input() items: T[] = [];
    @Input() isAutoCompleteWidthPanelWidthUnset: boolean;
    @Input() required: boolean;
    @Input() errorTextLabel: string;
    @Input() hintLabel: string;
    @Input() searchDisabled: boolean;
    @Input() togglePanelIconHidden: boolean;
    @Input() mainTextFontSize: TextFontSize;
    @Input() additionalTextFontSize: TextFontSize;
    @Input() appearance: MatFormFieldAppearance = 'fill';
    @Input() panelClass: string = '';
    @Input() formFieldClass: string = '';

    @Input() trackByFn: (item: T) => TRes = (item) => item['Id'];

    // resolve promise with 'result: false' for cancel selecting item
    @Input() canSelectItemFn: FiDropdownActionGuard<T> = () => Promise.resolve({ result: true });

    // resolve promise with 'result: false' for cancel deleting item
    @Input() canRemoveItemFn: FiDropdownActionGuard<T> = () => Promise.resolve({ result: true });

    @Input() isOptionWarningTextDisplayedFn: FiDropdownItemDisabledFn<T> = () => false;

    // get option title attribute in dropdown
    @Input() getOptionTitleFn: FiDropdownItemTitleFn<T> = () => undefined;

    // determine if we can remove item from selected items
    @Input() isRemoveItemDisabledFn: FiDropdownItemDisabledFn<T> = () => false;

    @Input() getOptionTextClass: (item: T) => string = () => '';

    @Input() isFullWidthOptions: boolean = true;
    @Input() label = '';
    @Input() profilePictureIcon: string;
    @Input() additionalDisplayValue: string;
    @Input() initialFocus: boolean = false;
    @Input() initialFocusDelay: number = 0;
    @Input() placeholder = '';
    @Input() clearOnLastItemClick: boolean = false;
    @Input() updatePositionOnScroll: boolean = true;
    @Input() isAddNewItemOptionShown: boolean = false;
    @Input() optionWarningText: string = '';
    @Input() addNewItemText: string = '';
    @Input() includeDisabledToSearchResult = true;
    @Output() selectedItemClick = new EventEmitter<T>();
    @Output() onAddNewItem = new EventEmitter<string>();

    @ViewChild('searchInput', { static: true }) searchInputRef: ElementRef<HTMLInputElement>;
    @ViewChild(MatAutocompleteTrigger, { read: MatAutocompleteTrigger }) inputAutoComplete: MatAutocompleteTrigger;
    @ViewChild('chipGrid') chipGrid: MatChipGrid;
    FiProfileSizes = FiProfileSizes;

    @Input() autoCompletePanelOpenedIconName: string = 'fas fa-chevron-up';
    @Input() autoCompletePanelClosedIconName: string = 'fas fa-chevron-down';
    private readonly userDisplayValuePropertyName: string = 'FullName';

    protected elementRef: ElementRef;
    protected fiBrowserEventsService: FiBrowserEventsService;
    protected fiChipsSelectSearchHelperService = inject(FiChipsSelectSearchHelperService);

    constructor(injector: Injector) {
        super(injector);
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.setServices();
        this.onInitialFocus();
        this.setEventSubscriptions();
        this.updateSelectedItemsFromModel();
        this.updateFilteredItems();
        this.subscribeOnErrorCheck();
    }

    private setServices() {
        this.elementRef = this.injector.get(ElementRef);
        this.fiBrowserEventsService = this.injector.get(FiBrowserEventsService);
    }

    private onInitialFocus() {
        if (this.initialFocus) {
            setTimeout(() => {
                this.openPanel();
            }, this.initialFocusDelay);
        }
    }

    private setEventSubscriptions() {
        this.fiBrowserEventsService
            .getDocumentEventSubject$('click')
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((event: Event) => {
                if (!this.elementRef.nativeElement.contains(event.target) && this.clearOnLastItemClick) {
                    this.clearInputValue();
                    this.closePanel();
                }
            });

        this.fiBrowserEventsService
            .getWindowEventSubject$('scroll', true)
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => {
                if (this.inputAutoComplete.panelOpen && this.updatePositionOnScroll)
                    this.inputAutoComplete.updatePosition();
            });
    }

    private subscribeOnErrorCheck() {
        this.fiChipsSelectSearchHelperService
            .onChipsSelectErrorStateCheck$()
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => this.setChipGridErrorState());
    }

    getOptionTitle(item: T): string {
        return this.getOptionTitleFn(item, this.items);
    }

    isOptionWarningTextDisplayed(item: T): boolean {
        return this.isOptionWarningTextDisplayedFn(item, this.items);
    }

    getAddNewItemOptionText(): string {
        return `${this.addNewItemText} "${this.searchText}"`;
    }

    addNewItem(): void {
        this.onAddNewItem.emit(this.searchText);
        this.clearInputValue();
    }

    isAddNewItemOptionVisible(): boolean {
        return this.isAddNewItemOptionShown && !isDefined(this.getItemBySearchText());
    }

    private getItemBySearchText(): T {
        return this.items.find((item: T) => {
            return this.searchText && item[this.displayValue].toLowerCase() === this.searchText.toLowerCase();
        });
    }

    getPlaceholder(): string {
        return !this.selectedItems$.value.length || document.activeElement === this.searchInputRef.nativeElement
            ? this.placeholder
            : '';
    }

    getAdditionalName(item: T): string {
        return this.additionalDisplayValue ? item[this.additionalDisplayValue] : undefined;
    }

    onItemSelected(item: T, $event: MatOptionSelectionChange) {
        super.onItemSelected(item, $event);

        this.canSelectItemFn({
            item: item,
            isSelected: true,
        }).then((dropdownActionResult: FiDropdownActionResult) => {
            if (!dropdownActionResult.result) return;

            this.toggleSelectedItem(item);
            this.setNgModelBySelectedItems();
            this.clearInputValue();
        });

        /*this.searchInputRef.nativeElement.value = '';
        this.searchInputRef.nativeElement.dispatchEvent(new Event('input', {
            'bubbles': true,
            'cancelable': true,
        }));*/

        //this.searchInputRef.nativeElement.focus();

        // https://chainformation.atlassian.net/browse/FI-5904
        // restore scroll top after reopening panel
        const panelScrollTop = this.inputAutoComplete.autocomplete._getScrollTop();
        setTimeout(() => {
            this.openPanel();
            setTimeout(() => {
                const panelElement: HTMLElement = this.inputAutoComplete.autocomplete.panel?.nativeElement;
                if (panelElement) {
                    panelElement.scrollTop = panelScrollTop;
                }
            });
        });
    }

    private toggleSelectedItem(item: T) {
        const selectedItems: T[] = this.selectedItems$.value.slice(0);
        const idx = selectedItems.findIndex(
            (selectedItem: T) => this.getItemValue(item) === this.getItemValue(selectedItem),
        );
        if (idx === -1) {
            selectedItems.push(item);
        } else {
            selectedItems.splice(idx, 1);
        }
        this.selectedItems$.next(selectedItems);
        this.setChipGridErrorState();
    }

    onItemRemoved(itemToRemove: T) {
        super.onItemRemoved(itemToRemove);

        this.canRemoveItemFn({
            item: itemToRemove,
            isSelected: false,
        }).then((dropdownActionResult: FiDropdownActionResult) => {
            if (!dropdownActionResult.result) return;

            this.removeSelectedItem(itemToRemove);
            this.setNgModelBySelectedItems();
            this.updateFilteredItems();
        });
    }

    private removeSelectedItem(itemToRemove: T) {
        const selectedItems: T[] = this.selectedItems$.value.slice(0);
        selectedItems.splice(selectedItems.indexOf(itemToRemove), 1);
        this.selectedItems$.next(selectedItems);
        this.setChipGridErrorState();
    }

    setChipGridErrorState() {
        if (isDefined(this.chipGrid)) {
            this.chipGrid.errorState = !this.selectedItems$.value.length && this.required;
            this.cdr.markForCheck();
        }
    }

    onItemClick(item: T, $event: Event) {
        if (this.isOptionDisabled(item)) return;

        $event.stopPropagation();
        this.onItemSelected(item, undefined);

        if (this.clearOnLastItemClick && this.isLastElement(item)) {
            this.clearInputValue();
            this.searchInputRef.nativeElement.focus();
        }
    }

    protected isLastElement(item: T): boolean {
        return isEqual(item, this.filteredItems$.value[this.filteredItems$.value.length - 1]);
    }

    private clearInputValue() {
        this.searchText = '';
        this.searchInputRef.nativeElement.value = '';
        this.updateFilteredItems();
    }

    openPanel() {
        this.inputAutoComplete.openPanel();
        this.searchInputRef.nativeElement.focus();
        this.inputAutoComplete.updatePosition();
    }

    closePanel() {
        this.inputAutoComplete.closePanel();
    }

    togglePanel($event: MouseEvent) {
        $event.stopPropagation();
        this.inputAutoComplete.panelOpen ? this.closePanel() : this.openPanel();
    }

    isPanelOpened(): boolean {
        return this.inputAutoComplete.panelOpen;
    }

    isRemoveItemDisabled(item: T): boolean {
        return this.isRemoveItemDisabledFn(item, this.items);
    }

    getTogglePanelClass(): string {
        return this.inputAutoComplete && !this.inputAutoComplete.panelOpen
            ? this.autoCompletePanelClosedIconName
            : this.autoCompletePanelOpenedIconName;
    }

    protected getSelectedItemsFromModel(): T[] {
        return !isDefined(this.ngModel)
            ? []
            : this.items.filter((item: T) => this.ngModel.indexOf(this.getItemValue(item)) !== -1);
    }

    setNgModelBySelectedItems() {
        this.ngModel = this.selectedItems$.value.map((selectedItem) => this.getItemValue(selectedItem));
        this.doPropagateChanges();
    }

    doPropagateChanges() {
        super.doPropagateChanges();
    }

    onSelectedItemClick(item: T) {
        setTimeout(() => {
            if (!this.removingItemInProcess) {
                this.selectedItemClick.emit(item);
            }
        });
    }

    getProfileData(item: T): IUserProfile {
        // todo refactor this after implementing https://chainformation.atlassian.net/browse/FI-4043 everywhere
        return this.displayValue === this.userDisplayValuePropertyName ? (item as IUserProfile) : undefined;
    }

    protected updateFilteredItems() {
        this.filteredItems$.next(this.getFilteredItems());
    }

    protected getFilteredItems(): T[] {
        return this.items.filter(
            (item: T) =>
                !this.isItemSelected(item) &&
                (this.isItemNameIncludesSearchText(item) || this.isItemAdditionalNameIncludesSearchText(item)) &&
                (this.includeDisabledToSearchResult || !this.searchText || !this.isOptionDisabled(item)),
        );
    }

    protected isItemSelected(item): boolean {
        return this.selectedItems$.value.some(
            (selectedItem) => this.getItemValue(selectedItem) === this.getItemValue(item),
        );
    }

    protected isItemNameIncludesSearchText(item: T): boolean {
        return !this.searchText || item[this.displayValue].toLowerCase().includes(this.searchText.toLowerCase());
    }

    private isItemAdditionalNameIncludesSearchText(item: T): boolean {
        return (
            this.additionalDisplayValue &&
            item[this.additionalDisplayValue].toLowerCase().includes(this.searchText.toLowerCase())
        );
    }

    protected getItemValue(item: T): TRes {
        return this.trackByFn(item);
    }

    getAutocompletePanelClass(): string[] {
        let classNames: string[] = ['fi-select-search-panel'];
        if (this.panelClass) {
            classNames = [...classNames, ...this.panelClass.split(' ')];
        }
        if (this.isFullWidthOptions) {
            classNames.push('full-width-options');
        }

        return classNames;
    }

    onAutocompleteClosed() {}

    onAutocompleteOpened() {}
}
